'use client';

import { useSuspenseQuery } from '@apollo/client/react';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { GET_ME_VIDEO_GENERATIONS } from '@/.graphql';

import { SerializedFile, serializeFiles } from '../utils/serializeFile';
import { createCustomContext } from './context';

interface IVideoGenerationProps {
  videos: SerializedFile[];
  activeVideoFile: File | null;
  setActiveVideoFile: (file: File | null) => void;
  activeVideo: SerializedFile;
  setActiveVideo: (video: SerializedFile) => void;
  loadMoreVideos: () => void;
  hasMoreVideos: boolean;
  isLoadingVideos: boolean;
  getVideoById: (id: string) => SerializedFile | undefined;
}

const [VideoContext, useVideoGenerationContext] =
  createCustomContext<IVideoGenerationProps>({
    name: 'VideoGenerationContext',
    strict: false,
  });

export { useVideoGenerationContext };

export const VideoGenerationProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    data: videoData,
    fetchMore: fetchMoreVideos,
    networkStatus: videoNetworkStatus,
  } = useSuspenseQuery(GET_ME_VIDEO_GENERATIONS, {
    variables: {
      cursor: null,
    },
  });

  // Мемоизируем сериализованные данные
  const videos = useMemo(
    () => serializeFiles(videoData?.getMeVideoGenerations?.data || []),
    [videoData],
  );

  // Получаем курсор
  const videoNextCursor = videoData?.getMeVideoGenerations?.nextCursor ?? null;
  const hasMoreVideos = !!videoNextCursor;

  // Статус загрузки для fetchMore
  const isLoadingVideos = videoNetworkStatus === 3; // NetworkStatus.fetchMore

  const loadMoreVideos = useCallback(() => {
    if (!hasMoreVideos || isLoadingVideos) {
      return;
    }

    return fetchMoreVideos({
      variables: {
        cursor: videoNextCursor,
      },
    });
  }, [hasMoreVideos, isLoadingVideos, videoNextCursor, fetchMoreVideos]);

  const [activeVideo, setActiveVideo] = useState<SerializedFile>(videos[0]);
  const [activeVideoFile, setActiveVideoFile] = useState<File | null>(null);

  // Utility functions
  const getVideoById = useCallback(
    (id: string) =>
      videos.find(
        (video) =>
          'videoGenerationId' in video && video.videoGenerationId === id,
      ),
    [videos],
  );

  const contextValue = useMemo(
    () => ({
      videos,
      activeVideo,
      setActiveVideo,
      loadMoreVideos,
      hasMoreVideos,
      isLoadingVideos,
      getVideoById,
      activeVideoFile,
      setActiveVideoFile,
    }),
    [
      videos,
      activeVideo,
      setActiveVideo,
      loadMoreVideos,
      hasMoreVideos,
      isLoadingVideos,
      getVideoById,
      activeVideoFile,
      setActiveVideoFile,
    ],
  );

  return <VideoContext value={contextValue}>{children}</VideoContext>;
};
